import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  AlertTriangle, MapPin, Clock, Eye, Search, Shield, ShieldCheck,
  CheckCircle, XCircle, Flag, Trash2, Menu, X, User, LogOut,
  LayoutDashboard, CreditCard, Settings, Filter, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function ModeratorPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flagFilter, setFlagFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [actionModal, setActionModal] = useState({ open: false, type: null, caseId: null });
  const [warningMessage, setWarningMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'moderator') {
      navigate('/dashboard');
      return;
    }
    fetchAllCases();
  }, [user]);

  const fetchAllCases = async () => {
    try {
      const response = await axios.get(`${API}/moderator/cases?skip=0&limit=200`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      // New paginated response: { items, total, skip, limit }
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.items || []);
      setCases(list);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCase = async (caseId) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/approve`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
    } catch (error) {
      console.error('Failed to approve case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFlagCase = async (caseId) => {
    if (!warningMessage.trim()) {
      alert('Please provide a warning message');
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/flag`, {
        warning_message: warningMessage,
        delete_after_48h: true
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
      setWarningMessage('');
    } catch (error) {
      console.error('Failed to flag case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/moderator/cases/${caseId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to delete case';
      alert(msg);
      console.error('Failed to delete case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnflagCase = async (caseId) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/unflag`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
    } catch (error) {
      console.error('Failed to unflag case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectCase = async (caseId) => {
    if (!warningMessage.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/reject`, {
        reason: warningMessage
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
      setWarningMessage('');
    } catch (error) {
      console.error('Failed to reject case:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveGuardianship = async (caseId) => {
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/guardianship/approve`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to approve guardianship';
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectGuardianship = async (caseId) => {
    if (!warningMessage.trim()) {
      alert('Please provide a rejection reason so the reporter can appeal.');
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${API}/moderator/cases/${caseId}/guardianship/reject`, {
        reason: warningMessage
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchAllCases();
      setActionModal({ open: false, type: null, caseId: null });
      setWarningMessage('');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to reject guardianship';
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const openGuardianshipDoc = async (caseId) => {
    try {
      const resp = await axios.get(`${API}/moderator/cases/${caseId}/guardianship`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const doc = resp.data?.guardianship_document;
      if (!doc) {
        alert('No guardianship document was uploaded for this case.');
        return;
      }
      // Convert the data: URI to a Blob and open via blob: URL. This avoids
      // `document.write` (no HTML injection surface) and lets the browser
      // render the asset natively.
      const match = /^data:([^;]+);base64,(.*)$/.exec(doc);
      if (!match) {
        alert('Unsupported document encoding.');
        return;
      }
      const mime = match[1];
      const byteString = atob(match[2]);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i += 1) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener,noreferrer');
      if (!w) {
        URL.revokeObjectURL(url);
        alert('Please allow pop-ups to view the document.');
        return;
      }
      // Release the object URL after a delay so the new window has time to
      // load it but we don't leak memory forever.
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to load guardianship document';
      alert(msg);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Countdown logic for flagged cases
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const getCountdown = (deleteAtStr) => {
    if (!deleteAtStr) return null;
    const deleteAt = new Date(deleteAtStr).getTime();
    const diff = deleteAt - now;
    if (diff <= 0) return { expired: true, label: 'Window expired — ready to delete' };
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return { expired: false, label: `${hours}h ${minutes}m remaining` };
  };

  const canModeratorDelete = (caseItem) => {
    if (user?.role === 'admin') return true;
    if (!caseItem.is_flagged) return false;
    const cd = getCountdown(caseItem.delete_at);
    return cd?.expired === true;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'bg-green-100 text-green-700' },
      pending_review: { label: 'Pending Review', class: 'bg-amber-100 text-amber-700' },
      flagged: { label: 'Flagged', class: 'bg-red-100 text-red-700' },
      rejected: { label: 'Rejected', class: 'bg-slate-100 text-slate-700' },
      under_investigation: { label: 'Under Investigation', class: 'bg-blue-100 text-blue-700' },
      located_safe: { label: 'Found Safe', class: 'bg-emerald-100 text-emerald-700' },
      closed: { label: 'Closed', class: 'bg-slate-100 text-slate-700' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getRoleBadge = () => {
    const roleConfig = {
      admin: { label: 'Admin', class: 'bg-red-100 text-red-700', icon: Shield },
      moderator: { label: 'Moderator', class: 'bg-purple-100 text-purple-700', icon: ShieldCheck },
    };
    const config = roleConfig[user?.role] || roleConfig.moderator;
    const IconComponent = config.icon;
    return (
      <Badge className={`${config.class} flex items-center gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          c.case_id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesFlag = flagFilter === 'all' || 
                        (flagFilter === 'flagged' && c.is_flagged) ||
                        (flagFilter === 'clean' && !c.is_flagged);
    return matchesSearch && matchesStatus && matchesFlag;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-black text-slate-900">MISYOU</span>
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mt-3">
              {getRoleBadge()}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <Link to="/moderator" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-purple-50 text-purple-700 font-medium">
              <ShieldCheck className="w-5 h-5" />
              Moderator Panel
            </Link>
            <Link to="/search" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <Eye className="w-5 h-5" />
              Search Cases
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
                <Settings className="w-5 h-5" />
                Admin Panel
              </Link>
            )}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-200">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 text-purple-600" />
                Moderator Panel
              </h1>
              <p className="text-muted-foreground mt-1">Review, approve, flag, or remove cases</p>
            </div>
            <NotificationBell />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Total Cases</p>
                <p className="text-2xl font-bold text-slate-900">{cases.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold text-amber-600">
                  {cases.filter(c => c.status === 'pending_review').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Flagged</p>
                <p className="text-2xl font-bold text-red-600">
                  {cases.filter(c => c.is_flagged).length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-600">
                  {cases.filter(c => c.status === 'active').length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-6 bg-white">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or case ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="flagged">Flagged</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="located_safe">Found Safe</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={flagFilter} onValueChange={setFlagFilter}>
                  <SelectTrigger className="w-full md:w-40">
                    <SelectValue placeholder="Flag status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="flagged">Flagged Only</SelectItem>
                    <SelectItem value="clean">Clean Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Cases List */}
          <Card className="bg-white">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg">All Cases ({filteredCases.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground">Loading cases...</div>
              ) : filteredCases.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No cases found</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCases.map((caseItem) => (
                    <div key={caseItem.case_id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start gap-4">
                        {/* Photo */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                          {caseItem.photos && caseItem.photos.length > 0 ? (
                            <img 
                              src={caseItem.photos[0]} 
                              alt={caseItem.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{caseItem.full_name}</h3>
                            {getStatusBadge(caseItem.status)}
                            {caseItem.is_flagged && (
                              <Badge className="bg-red-100 text-red-700">
                                <Flag className="w-3 h-3 mr-1" />
                                Flagged
                              </Badge>
                            )}
                            {caseItem.guardianship_review_status === 'pending' && (
                              <Badge
                                className="bg-indigo-100 text-indigo-800 border border-indigo-300"
                                data-testid={`guardianship-pending-${caseItem.case_id}`}
                              >
                                Guardianship: pending
                              </Badge>
                            )}
                            {caseItem.guardianship_review_status === 'approved' && (
                              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                                Guardianship: verified
                              </Badge>
                            )}
                            {caseItem.guardianship_review_status === 'rejected' && (
                              <Badge className="bg-red-100 text-red-700 border border-red-300">
                                Guardianship: rejected
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            Case ID: {caseItem.case_id}
                          </p>
                          <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                            {caseItem.age && <span>Age: {caseItem.age}</span>}
                            {caseItem.last_seen_location?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {caseItem.last_seen_location.city}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(caseItem.created_at)}
                            </span>
                          </div>
                          {caseItem.warning_message && (
                            <Alert className="mt-2 bg-amber-50 border-amber-200">
                              <AlertTriangle className="w-4 h-4 text-amber-600" />
                              <AlertDescription className="text-amber-700 text-sm">
                                <strong>Warning:</strong> {caseItem.warning_message}
                                {caseItem.delete_at && (() => {
                                  const cd = getCountdown(caseItem.delete_at);
                                  return (
                                    <span className={`block text-xs mt-1 font-medium ${cd?.expired ? 'text-red-700' : 'text-amber-800'}`}>
                                      Auto-delete window: {formatDate(caseItem.delete_at)} • {cd?.label}
                                    </span>
                                  );
                                })()}
                              </AlertDescription>
                            </Alert>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 shrink-0">
                          <Link to={`/case/${caseItem.case_id}`}>
                            <Button variant="outline" size="sm" className="w-full">
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </Link>
                          {caseItem.guardianship_review_status === 'pending' && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-indigo-700 border-indigo-200 hover:bg-indigo-50 w-full"
                                onClick={() => openGuardianshipDoc(caseItem.case_id)}
                                data-testid={`view-guardianship-btn-${caseItem.case_id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View guardianship doc
                              </Button>
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 w-full"
                                onClick={() => handleApproveGuardianship(caseItem.case_id)}
                                disabled={actionLoading}
                                data-testid={`approve-guardianship-btn-${caseItem.case_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Verify guardianship
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50 w-full"
                                onClick={() => {
                                  setActionModal({ open: true, type: 'guardianship_reject', caseId: caseItem.case_id });
                                  setWarningMessage('');
                                }}
                                data-testid={`reject-guardianship-btn-${caseItem.case_id}`}
                              >
                                <AlertTriangle className="w-4 h-4 mr-1" />
                                Reject guardianship
                              </Button>
                            </>
                          )}
                          {caseItem.status === 'pending_review' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700 w-full"
                              onClick={() => handleApproveCase(caseItem.case_id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          )}
                          {!caseItem.is_flagged && caseItem.status !== 'rejected' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-amber-600 border-amber-200 hover:bg-amber-50 w-full"
                              onClick={() => {
                                setActionModal({ open: true, type: 'flag', caseId: caseItem.case_id });
                                setWarningMessage('');
                              }}
                              data-testid={`flag-case-btn-${caseItem.case_id}`}
                            >
                              <Flag className="w-4 h-4 mr-1" />
                              Flag (48h)
                            </Button>
                          )}
                          {caseItem.is_flagged && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 w-full"
                              onClick={() => handleUnflagCase(caseItem.case_id)}
                              disabled={actionLoading}
                              data-testid={`unflag-case-btn-${caseItem.case_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Unflag
                            </Button>
                          )}
                          {(() => {
                            const enabled = canModeratorDelete(caseItem);
                            const isAdmin = user?.role === 'admin';
                            const cd = getCountdown(caseItem.delete_at);
                            const tooltip = !enabled
                              ? (caseItem.is_flagged
                                  ? `Wait ${cd?.label || '48h'} before deleting`
                                  : 'Flag this case with a 48-hour warning before deleting')
                              : '';
                            return (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => setActionModal({ open: true, type: 'delete', caseId: caseItem.case_id })}
                                disabled={!enabled}
                                title={tooltip}
                                data-testid={`delete-case-btn-${caseItem.case_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                {isAdmin ? 'Delete (Admin)' : 'Delete'}
                              </Button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Action Modal */}
      {actionModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {actionModal.type === 'flag' && (
                  <>
                    <Flag className="w-5 h-5 text-amber-600" />
                    Flag Case
                  </>
                )}
                {actionModal.type === 'delete' && (
                  <>
                    <Trash2 className="w-5 h-5 text-red-600" />
                    Delete Case
                  </>
                )}
                {actionModal.type === 'reject' && (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    Reject Case
                  </>
                )}
                {actionModal.type === 'guardianship_reject' && (
                  <>
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Reject Guardianship
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {actionModal.type === 'flag' && (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Send a 48-hour warning to the case owner. They will see this message on their dashboard
                    and have 48 hours to address it. After that period, a moderator may permanently delete
                    the case (or admins can override at any time).
                  </p>
                  <Textarea
                    placeholder="Explain why this case is being flagged (visible to the user)..."
                    value={warningMessage}
                    onChange={(e) => setWarningMessage(e.target.value)}
                    className="mb-4"
                    rows={4}
                    data-testid="flag-warning-message-input"
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ open: false, type: null, caseId: null })}
                      className="flex-1"
                      data-testid="flag-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-amber-600 hover:bg-amber-700"
                      onClick={() => handleFlagCase(actionModal.caseId)}
                      disabled={actionLoading}
                      data-testid="flag-confirm-btn"
                    >
                      {actionLoading ? 'Flagging...' : 'Send 48h Warning'}
                    </Button>
                  </div>
                </>
              )}
              {actionModal.type === 'delete' && (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    {user?.role === 'admin'
                      ? 'You are deleting this case as Admin. This bypasses the 48-hour warning rule and cannot be undone.'
                      : 'The 48-hour warning window has passed. This will permanently delete the case and its sightings. This action cannot be undone.'}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ open: false, type: null, caseId: null })}
                      className="flex-1"
                      data-testid="delete-cancel-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={() => handleDeleteCase(actionModal.caseId)}
                      disabled={actionLoading}
                      data-testid="delete-confirm-btn"
                    >
                      {actionLoading ? 'Deleting...' : 'Delete Permanently'}
                    </Button>
                  </div>
                </>
              )}
              {actionModal.type === 'guardianship_reject' && (
                <>
                  <p className="text-sm text-muted-foreground mb-4">
                    Rejecting guardianship will <strong>withdraw the case from public listings</strong> and notify the reporter with your reason. Use this when the uploaded document does not credibly establish guardianship of the child.
                  </p>
                  <Textarea
                    placeholder="Explain why guardianship couldn't be verified (the reporter will see this)..."
                    value={warningMessage}
                    onChange={(e) => setWarningMessage(e.target.value)}
                    className="mb-4"
                    rows={4}
                    data-testid="reject-guardianship-reason-input"
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ open: false, type: null, caseId: null })}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={() => handleRejectGuardianship(actionModal.caseId)}
                      disabled={actionLoading}
                      data-testid="reject-guardianship-confirm-btn"
                    >
                      {actionLoading ? 'Rejecting...' : 'Reject guardianship'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
